<template>
  <div
    ref="currentTable"
    :class="['common-table', `${tableId}_common-table`, {'y-scroll-auto':yScrollAuto}]"
    :tabletype="getTableType()"
  >
    <div
      ref="currentTableHeader"
      :class="['common-table-header-container', {'min-width-1800px': minWidth1800px}]"
    >
      <tr
        ref="currentTableHeaderTitle"
        class="common-header-title"
      >
        <th
          v-for="item in renderHeaderList"
          :key="`${item.title}_${item.name}`"
          :class="['common-table__head-th', {'hand': !!formatList.length}, item.name]"
          @click="changeOrderKeyOrSequence(item.name)"
        >
          <span>
            {{ item.title }}
            <i
              v-if="item.name === orderKey && formatList.length"
              :class="[needOrder ? (order ? 'triangle-up' : 'triangle-down') : '']"
            />
          </span>
        </th>
      </tr>
      <tr
        v-if="needFilter || needSearch"
        class="common-header-filter"
      >
        <th
          v-for="item in renderHeaderList"
          :key="item.title"
          :class="item.name"
        >
          <SearchFilter
            :cls="item.cls"
            :inputType="item.inputType"
            :options="item.options"
            :groupValues="item.groupValues"
            :groupLabel="item.groupLabel"
            :customLabel="item.customLabel"
            :onchange="changeConditions"
            :type="item.trackBy || item.name"
            :value="getFilterDefaultValue(item)"
            :enterEvent="enterEvent"
            :singleDatePicker="!!item.singleDatePicker"
            :trackBy="item.trackBy"
            :multiLabel="item.multiLabel"
            :multiple="!!item.multiple"
            :component="item.component"
            :searchable="item.searchable"
            :normalizeEmptySelectToAll="!!item.normalizeEmptySelectToAll"
            :dateFromPlaceholder="item.dateFromPlaceholder"
            :dateToPlaceholder="item.dateToPlaceholder"
            :disabled="item.disabledSearch || disabledAllSearchFilter"
            :disabledDate="item.disabledDate"
            :disabledDateRangeBySelect="item.disabledDateRangeBySelect"
            :datePickerCanPutTotop="item.datePickerCanPutTotop"
          />
        </th>
      </tr>
    </div>
    <TableContent
      ref="currentTableContent"
      :class="['search-filter-conditions', 'common-table-content-container']"
      :renderHeaderList="renderHeaderList"
      :tableContentLists="formatList"
      :interactionFunc="interactionFunc"
      :orderByDateKeys="orderByDateKeys"
    />
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapMutations, mapGetters } from 'vuex'
import { ONE_DAY_MILLI } from '@/utils/dateUtil'
import SearchFilter from '@/modules/contacts/SearchFilter'
import { INPUT_TYPE } from '@/modules/contacts/searchFilter.const'
import mutationTypes from '@/store/mutation-types'
import { MIN_STRING, MAX_STRING, COMMON_TABLE_SELECT_FILTER_OPTION_ALL } from '@/utils/locale'
import TableContent from './TableContent'

export default {
  name: 'CommonTable',
  components: {
    SearchFilter,
    TableContent,
  },
  props: {
    tableContentLists: {
      type: Array,
      default: () => [],
    },
    disabledAllSearchFilter: {
      type: Boolean,
      default: false,
    },
    /**
     * content order & cls
     * [{
     *  name: string //to be td class name and should has width
     *  cls: when cls is a prop, cls is class name has more priority than name
     *  title: string // display in td
     *  inputType: search or filter type
     *  formatRule: current data show format as formatRule
     *  multiLabel: when inputType is select and options is a object.
     *  options: when inputType is select, it is the multiSelect options,
     *  defaultValue: display in td when the data is equal null or '' or undefined
     *  dateFromPlaceholder: fore date select placeholder when inputType is date
     *  dateToPlaceholder: behind date select placeholder when inputType is date
     * }]
     * */
    renderHeaderList: {
      type: Array,
      required: true,
    },
    /**
     * td clickEvent
     * [{name, func}]
     * */
    interactionFunc: {
      type: [Object, String],
      default: '',
    },
    // table content list length
    getDisplayListLength: {
      type: Function,
      default: () => {},
    },
    defaultOrderKey: {
      type: String,
      default: '',
    },
    // get all current conditions
    getConditions: {
      type: Function,
      default: () => {},
    },
    defaultConditions: {
      type: [Object, String],
      default: '',
    },
    defaultOrder: {
      type: Boolean,
      default: false,
    },
    getTableOrder: {
      type: Function,
      default: () => {},
    },
    // needFilter is mutual exclusion with needSearch
    needFilter: {
      type: Boolean,
      default: false,
    },
    needSearch: {
      type: Boolean,
      default: false,
    },
    tableId: {
      // must be unique
      type: String,
      required: true,
    },
    // save table info into vuex or somewhere
    setTableInfo: {
      type: [Function, String],
      default: '',
    },
    needSaveTableInfo: {
      type: Boolean,
      default: true,
    },
    // if need read data from vuex when table mounted
    needCache: {
      type: Boolean,
      default: true,
    },
    afterResizeTable: {
      type: Function,
      default: () => {},
    },
    // input search or filter press enter button exclude mulitSelect and date select
    enterEvent: {
      type: Function,
      default: () => {},
    },
    // special order keys
    orderByDateKeys: {
      type: Array,
      default: () => [],
    },
    orderByNumberKeys: {
      type: Array,
      default: () => [],
    },
    /*
      customer order methods
      {
        name: orderMethod
      }
    */
    orderMethods: {
      type: Object,
      default: () => {},
    },
    // show order trangle
    needOrder: {
      type: Boolean,
      default: true,
    },
    // for specific table, for example performance which has extra condition, it needs format data by itself
    afterMounted: {
      type: Function,
      default: () => {},
    },
    // When the Table Content changes, resize the Column width.
    resizeColumnWhenContentChange: {
      type: Boolean,
      default: true,
    },
    yScrollAuto: {
      type: Boolean,
      default: false,
    },
    // This is the fix for default min-width: 1800px, would be removed in the future, use it carefully
    minWidth1800px: {
      type: Boolean,
      default: true,
    },
  },
  data () {
    return {
      formatList: [],
      tableContentListsData: [],
      order: this.defaultOrder, // false true
      orderKey: this.defaultOrderKey,
      filterConditions: '', // filter & search both use it
      debounceResize: lodash.debounce(this.resizeTable, 40),
    }
  },
  computed: {
    ...mapGetters('tables', ['getTableInfoById']),
    currentTable () {
      return this.$refs.currentTable
    },
    currentTableHeader () {
      return this.$refs.currentTableHeader
    },
    currentTableContent () {
      return this.$refs.currentTableContent.$el
    },
  },
  watch: {
    formatList (nVal, oVal) {
      !lodash.isEqual(nVal, oVal) && this._setTableInfo()
    },
    tableContentLists () {
      this.initTableContentListsFromProps()
      this.doFormatList()
    },
    // just for reset filters / search conditions
    defaultConditions () {
      if (!this.defaultConditions) {
        this.filterConditions = {}
      } else {
        this.initFilterConditions()
      }
    },
    tableId () {
      this.initFilterConditions()
      this.doFormatList()
    },
  },
  async mounted () {
    if (this.needFilter && this.needSearch) {
      console.error(`Please checkout commonTable props needSearch and needFilter, they are mutual exclusion! In table ${this.tableId}`)
    }
    window.addEventListener('resize', this.debounceResize)
    this.initTableContentListsFromCache()
    this.initFilterConditions()
    this.doFormatList()
    this._afterMounted()
    await this.debounceResize()
    this.scrollToLastPosition()
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.debounceResize)
  },
  methods: {
    ...mapMutations('tables', {
      setTableInfoIntoStore: mutationTypes.TABLE_SET_TABLE_INFO,
    }),
    getTableType () {
      return this.needFilter ? 'filter' : this.needSearch ? 'search' : ''
    },
    getFilterDefaultValue (item) {
      if (this.filterConditions && this.filterConditions[item.name] && !item.groupValues) {
        return this.filterConditions[item.name].value
      }
      // HKLSelect: undefined = empty; explicit '' is used only by filters that intentionally default to All.
      if (Object.prototype.hasOwnProperty.call(item, 'defaultFilterValue')) {
        return item.defaultFilterValue
      }
      if (item.component) {
        return undefined
      }
      if (item.inputType === INPUT_TYPE.SELECT) {
        return undefined
      }
      return ''
    },
    changeOrderKeyOrSequence (key) {
      this.orderKey = key
      this.order = !this.order
      this.doFormatList()
      this.getTableOrder(this.orderKey, this.order)
    },
    changeKeyAndOrder (key, order) {
      this.orderKey = key
      this.order = order
      this.doFormatList()
      this.getTableOrder(this.orderKey, this.order)
    },
    doSort () {
      const defaultOrderIteratee = 'id'
      const iterateeArr = [item => this.sortByKey(item)]
      const hasIdAttribute = lodash.has(this.tableContentListsData, `0.${defaultOrderIteratee}`)
      if (hasIdAttribute) {
        iterateeArr.push(defaultOrderIteratee)
      }
      const result = lodash.orderBy(this.tableContentListsData, iterateeArr)
      return this.order ? result : result.reverse()
    },
    sortByKey (item) {
      let lastOne = this.order ? Number.MAX_SAFE_INTEGER : Number.MIN_SAFE_INTEGER
      if (!lodash.isEmpty(this.orderMethods) && typeof this.orderMethods[this.orderKey] === 'function') {
        return this.orderMethods[this.orderKey](item, this.order, this.orderKey)
      } else {
        const value = item[this.orderKey]
        switch (true) {
          case item.inputType === INPUT_TYPE.DATE:
          case this.orderByDateKeys.includes(this.orderKey):
            return value ? (new Date(value)).getTime() : lastOne
          case this.orderByNumberKeys.includes(this.orderKey):
            return isNaN(value) ? lastOne : value - 0
          case typeof value === 'string':
            lastOne = this.order ? MAX_STRING : MIN_STRING
            return value ? value.toUpperCase() : lastOne
          case typeof value === 'number':
            return isNaN(value) ? lastOne : value - 0
          default:
            return value
        }
      }
    },
    doFormatList () {
      this.formatList = this.needFilter ? this.filterList(this.doSort()) : this.doSort()
      this.getDisplayListLength(this.formatList.length)
      if (this.resizeColumnWhenContentChange) {
        this.debounceResize()
      }
    },
    filterByInput (key, val, orderAndFilterList) {
      if (!val) {
        return orderAndFilterList
      }
      return orderAndFilterList.filter(item => {
        return item[key] && lodash.includes(item[key].toString().toLowerCase(), val.toString().toLowerCase())
      })
    },
    filterByDate (key, val, orderAndFilterList) {
      return orderAndFilterList.filter(item => {
        let qualified = true
        if (val[0] && new Date(item[key]) < val[0]) {
          qualified = false
        }
        if (val[1] && new Date(item[key]) > new Date(val[1].getTime() + ONE_DAY_MILLI - 1)) {
          qualified = false
        }
        return qualified
      })
    },
    filterBySelect (key, val, orderAndFilterList) {
      if (!val || val.toString().toLowerCase() === COMMON_TABLE_SELECT_FILTER_OPTION_ALL) {
        return orderAndFilterList
      }
      return orderAndFilterList.filter(item => item[key] === val)
    },
    filterList (orderList) {
      let orderAndFilterList = lodash.cloneDeep(orderList)
      for (let key in this.filterConditions) {
        switch (this.filterConditions[key].inputType) {
          case 'input':
            orderAndFilterList = this.filterByInput(key, this.filterConditions[key].value, orderAndFilterList)
            break
          case 'date':
            orderAndFilterList = this.filterByDate(key, this.filterConditions[key].value, orderAndFilterList)
            break
          case 'select':
            orderAndFilterList = this.filterBySelect(key, this.filterConditions[key].value, orderAndFilterList)
            break
        }
      }
      return orderAndFilterList
    },
    // input or select change the conditions
    changeConditions (condition) {
      this.filterConditions = Object.assign({}, this.filterConditions, condition)
      this.needFilter && this.doFormatList()
      this.getConditions(this.filterConditions)
    },
    hasVerticalScrollbar () {
      const trs = this.currentTableContentTrs()
      const trHeight = trs.reduce((total, tr) => {
        return total + tr.getBoundingClientRect().height
      }, 0)
      const contentHeight = this.currentTableContent.getBoundingClientRect().height
      return trHeight > contentHeight
    },
    resizeTable () {
      const isWin = navigator.platform === 'Win32' || navigator.platform === 'Win64'
      const hasScroll = this.hasVerticalScrollbar()
      const trs = this.currentTableContentTrs()
      const headerTrWidth = this.$refs.currentTableHeaderTitle
        ? this.$refs.currentTableHeaderTitle.getBoundingClientRect().width
        : 0
      if (headerTrWidth) {
        $(this.currentTableContent).css('width', headerTrWidth)
        $(trs).css('width', headerTrWidth)
      }
      this.afterResizeTable(isWin, hasScroll)
    },
    _setTableInfo () {
      if (this.needSaveTableInfo) {
        const tableInfo = {
          originUrl: location.href,
          tableId: this.tableId,
          scrollTop: lodash.get(this.currentTableContent, 'scrollTop', 0),
          filterConditions: this.filterConditions,
          tableContentLists: this.tableContentListsData,
          order: this.order,
          orderKey: this.orderKey,
        }
        if (this.setTableInfo) {
          this.setTableInfo(tableInfo)
        } else {
          this.setTableInfoIntoStore(tableInfo)
        }
      }
    },
    initTableContentListsFromCache () {
      this.tableContentListsData = lodash.cloneDeep(lodash.isEmpty(this.tableContentLists)
        ? (this.needCache ? lodash.get(this.getTableInfoById(this.tableId), 'tableContentLists')
          : [])
        : this.tableContentLists)
    },
    initTableContentListsFromProps () {
      this.tableContentListsData = lodash.cloneDeep(this.tableContentLists)
      $(this.currentTableContent).scrollTop(0)
    },
    initFilterConditions () {
      this.filterConditions = lodash.cloneDeep(this.defaultConditions || (this.needCache && lodash.get(this.getTableInfoById(this.tableId), 'filterConditions', '')))
      this.order = this.needCache ? lodash.get(this.getTableInfoById(this.tableId), 'order', this.defaultOrder) : this.defaultOrder
      this.orderKey = this.needCache ? lodash.get(this.getTableInfoById(this.tableId), 'orderKey', this.defaultOrderKey) : this.defaultOrderKey
      this.getConditions(this.filterConditions)
      this.getTableOrder(this.orderKey, this.order)
    },
    scrollToLastPosition () {
      const lastPosition = lodash.get(this.getTableInfoById(this.tableId), 'scrollTop', 0)
      $(this.currentTableContent).scrollTop(lastPosition)
    },
    currentTableContentTrs () {
      return Array.from($(this.currentTableContent).children('tr'))
    },
    /** Clear header filter inputs only; leaves sorting and row data unchanged. */
    resetFilterUiOnly () {
      this.filterConditions = {}
    },
    // Full reset (re-formats rows). Listing pages should call resetFilterUiOnly() instead.
    resetSearchOrFilter () {
      this.filterConditions = {}
      this.doFormatList()
    },
    _afterMounted () {
      this.afterMounted({
        conditions: this.filterConditions,
        tableContent: this.tableContentListsData || [],
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.common-table {
  overflow-y: hidden;
  overflow-x: auto;
  min-height: 100px;
  position: relative;
  margin: 15px 15px 0;
  border-collapse:collapse;
  display: flex;
  flex-direction: column;
  .multiselect__content-wrapper {
    font-size: 0;
    ul {
      font-size: 14px;
    }
  }
  &__head-th > span {
    font-weight: bold;
    text-transform: uppercase;
  }
  .hand {
    cursor: pointer;
  }
  th, td {
    vertical-align: middle;
    text-align: left;
    padding: 10px;
    font-size: 14px;
    min-height: 41px;
    display: inline-block;
  }
  tr {
    display: inline-block;
    border-bottom: 1px solid #e0e0e0;
    border-left: 1px solid #e0e0e0;
    border-right: 1px solid #e0e0e0;
  }
  th {
    ::v-deep .search-filter-input {
      display: block;
      width: 100%;
      max-width: 100%;
      min-width: 0;
      box-sizing: border-box;
    }
  }
  /**
  * NOTICE: outside could set the common-table-header-container min-width
  * The min-width was poor-designed at the beginning
  * Use prop minWidth1800px:false to skip `min-width: 1800px;`
  * */
  .min-width-1800px {
    min-width: 1800px;
  }
  .common-table-header-container {
    tr {
      border-top: none;
      display: block;
      width: 100%;
    }
    tr:first-child {
      border-top: 1px solid #e0e0e0;
    }
  }
  .common-header-filter {
    height: auto;
  }

  .common-header-filter th {
    text-transform: none;
    min-width: 0;
    box-sizing: border-box;
    height: auto;

    ::v-deep .search-area,
    ::v-deep .select-area,
    ::v-deep .input-area,
    ::v-deep .date-area,
    ::v-deep .range-area {
      width: 100%;
      max-width: 100%;
      box-sizing: border-box;
    }

    ::v-deep .multiselect,
    ::v-deep .hkl-select {
      min-width: 0;
      max-width: 100%;
      width: 100%;
    }

    ::v-deep .search-filter-input {
      min-height: 44px;
      height: auto;
    }

    ::v-deep .input-area .hkl-input-box,
    ::v-deep .input-area input {
      width: 100%;
      min-width: 0;
      box-sizing: border-box;
    }

    ::v-deep .input-area,
    ::v-deep .date-area {
      height: 44px;
    }
  }

  &[tableType='filter'],
  &[tableType='search'] {
    .common-table-content-container {
      flex-grow: 1;
    }
  }
  .common-table-content-container {

    overflow-x: hidden;
    overflow-y: auto;
  }
  .triangle {
    cursor: pointer;
    display: inline-block;
    height: 20px;
    width: 20px;
    border: 1px solid;
  }
  .triangle-up {
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-bottom: 10px solid #0C3A58;
    display: inline-block;
    margin-left: 5px;
  }
  .triangle-down {
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 10px solid #0C3A58;
    display: inline-block;
    margin-left: 5px;
  }
}
.y-scroll-auto{
  overflow-y: auto
}
</style>
